import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SearchableSelect from "../../components/ui/SearchableSelect";
import umService from "../../services/university-admin/umService";

const UMCampusForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation("universityManager");

  const [formData, setFormData] = useState({
    countryId: "",
    cityId: "",
    nameEn: "",
    nameLocal: "",
  });
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      const res = await umService.getCountries();
      if (res.success) setCountries(res.data);
      setLoadingCountries(false);
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!formData.countryId) {
      setCities([]);
      return;
    }
    const fetchCities = async () => {
      setLoadingCities(true);
      const res = await umService.getCitiesByCountry(formData.countryId);
      if (res.success) setCities(res.data);
      setLoadingCities(false);
    };
    fetchCities();
  }, [formData.countryId]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        countryId: initialData.countryId || "",
        cityId: initialData.cityId || "",
        nameEn: initialData.nameEn || "",
        nameLocal: initialData.nameLocal || "",
      });
    } else {
      setFormData({ countryId: "", cityId: "", nameEn: "", nameLocal: "" });
    }
    setErrors({});
  }, [initialData]);

  const validate = () => {
    const newErrors = {};
    if (!formData.countryId) newErrors.countryId = t("crud.errors.countryRequired");
    if (!formData.cityId) newErrors.cityId = t("crud.errors.cityRequired");
    if (!formData.nameEn.trim()) newErrors.nameEn = t("crud.errors.nameEnRequired");
    if (!formData.nameLocal.trim()) newErrors.nameLocal = t("crud.errors.nameLocalRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate() || loading) return;
    onSubmit({
      cityId: formData.cityId,
      nameEn: formData.nameEn.trim(),
      nameLocal: formData.nameLocal.trim(),
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "countryId") next.cityId = "";
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "countryId" && errors.cityId) setErrors((prev) => ({ ...prev, cityId: "" }));
  };

  const countryOptions = countries.map((c) => ({ value: c.id, label: c.name }));
  const cityOptions = cities.map((c) => ({ value: c.id, label: c.name }));

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Country & City */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.country")} <span className="text-danger">*</span>
          </label>
          <SearchableSelect
            options={countryOptions}
            value={formData.countryId}
            onChange={(val) => handleChange("countryId", val)}
            placeholder={t("crud.selectCountry")}
            searchPlaceholder={t("crud.searchCountry")}
            disabled={loading}
            isInvalid={!!errors.countryId}
            loading={loadingCountries}
            emptyMessage={t("crud.noCountriesFound")}
          />
          {errors.countryId && (
            <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
              {errors.countryId}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.city")} <span className="text-danger">*</span>
          </label>
          <SearchableSelect
            options={cityOptions}
            value={formData.cityId}
            onChange={(val) => handleChange("cityId", val)}
            placeholder={formData.countryId ? t("crud.selectCity") : t("crud.selectCountryFirst")}
            searchPlaceholder={t("crud.searchCity")}
            disabled={loading || !formData.countryId}
            isInvalid={!!errors.cityId}
            loading={loadingCities}
            emptyMessage={t("crud.noCitiesFound")}
          />
          {errors.cityId && (
            <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
              {errors.cityId}
            </div>
          )}
        </div>
      </div>

      {/* Campus Names */}
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.nameEn")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
            value={formData.nameEn}
            onChange={(e) => handleChange("nameEn", e.target.value)}
            placeholder={t("crud.placeholders.nameEn")}
            disabled={loading}
          />
          {errors.nameEn && <div className="invalid-feedback">{errors.nameEn}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label fw-medium">
            {t("crud.fields.nameLocal")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.nameLocal ? "is-invalid" : ""}`}
            value={formData.nameLocal}
            onChange={(e) => handleChange("nameLocal", e.target.value)}
            placeholder={t("crud.placeholders.nameLocal")}
            disabled={loading}
          />
          {errors.nameLocal && <div className="invalid-feedback">{errors.nameLocal}</div>}
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        {onCancel && (
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={loading}>
            <i className="fas fa-times me-2"></i>
            {t("crud.cancel")}
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              {t("crud.saving")}
            </>
          ) : (
            <>
              <i className={`fas ${initialData ? "fa-save" : "fa-plus"} me-2`}></i>
              {initialData ? t("crud.save") : t("crud.addCampus")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UMCampusForm;
